// src/store/themeSlice.js
// ============================================================
// ТЕМА ОФОРМЛЕНИЯ: светлая / тёмная.
//
// 🔑 ПОЧЕМУ REDUX, А НЕ ПРОСТО localStorage В КОМПОНЕНТЕ.
//    Тема нужна сразу в нескольких местах: сам переключатель, иконка
//    внутри него, и (в будущем) любые блоки, которые захотят знать
//    текущую тему. Держать это в локальном состоянии переключателя
//    значит рассинхронизировать копии.
//
// ⚠️ ПОЧЕМУ ЗДЕСЬ ЕСТЬ И `applyTheme`, И СИНХРОННАЯ ЗАПИСЬ В DOM.
//    Тема обязана примениться ДО первой отрисовки, иначе при загрузке
//    страницы мелькнёт светлый фон и только потом станет тёмным
//    (вспышка «FOUC»). См. `main.jsx` — там тема ставится до рендера.
// ============================================================

import { createSlice } from '@reduxjs/toolkit';

const STORAGE_KEY = 'studio-theme';

// Допустимые значения. 'system' — следовать за настройкой ОС.
export const THEMES = ['light', 'dark', 'system'];

/**
 * Вычисляет фактическую тему ('light' | 'dark') из выбора пользователя.
 * 'system' разворачивается в текущую настройку ОС.
 */
export const resolveTheme = (preference) => {
  if (preference === 'light' || preference === 'dark') return preference;
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

/**
 * Применяет тему к документу: атрибут `data-theme` на <html>.
 *
 * ⚠️ Ставим ИМЕННО `data-theme`, потому что от него зависят:
 *    • CSS-переменные в `globals.css` (`[data-theme='dark']`);
 *    • Tailwind `darkMode: ['selector', '[data-theme="dark"]']`.
 *    Если поменять имя атрибута в одном месте и забыть в другом,
 *    тёмная тема «не сработает» без единой ошибки в консоли.
 */
export const applyTheme = (theme) => {
  if (typeof document === 'undefined') return;
  const resolved = resolveTheme(theme);
  document.documentElement.setAttribute('data-theme', resolved);
};

/**
 * Что показывать пользователю при первом заходе — его выбор из
 * прошлого раза, а если его нет, то настройку ОС.
 */
const readStoredPreference = () => {
  if (typeof window === 'undefined') return 'system';
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return THEMES.includes(saved) ? saved : 'system';
  } catch {
    // Приватный режим / отключённое хранилище — не повод падать.
    return 'system';
  }
};

const initialState = {
  // Что выбрал пользователь: 'light' | 'dark' | 'system'.
  preference: readStoredPreference(),
  // Что реально применено с учётом системной настройки: 'light' | 'dark'.
  resolved: resolveTheme(readStoredPreference()),
};

const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    setTheme: (state, action) => {
      const preference = THEMES.includes(action.payload) ? action.payload : 'system';
      state.preference = preference;
      state.resolved = resolveTheme(preference);

      try {
        localStorage.setItem(STORAGE_KEY, preference);
      } catch {
        // Не смогли запомнить — тема всё равно применится на эту сессию.
      }

      applyTheme(preference);
    },

    /**
     * Системная настройка изменилась (событие matchMedia).
     * Реагируем ТОЛЬКО если пользователь выбрал 'system': иначе мы
     * перезапишем его явный выбор.
     */
    systemThemeChanged: (state, action) => {
      if (state.preference !== 'system') return;
      state.resolved = action.payload === 'dark' ? 'dark' : 'light';
      applyTheme(state.preference);
    },
  },
});

export const { setTheme, systemThemeChanged } = themeSlice.actions;

export const selectThemePreference = (state) => state.theme.preference;
export const selectResolvedTheme = (state) => state.theme.resolved;

export default themeSlice.reducer;
